import { memo, useCallback, useMemo, useState } from 'react';

import type { ResolutionEnumNoPrefix } from '@talos/kyoko';
import {
  Box,
  CardViewEnum,
  ConnectionType,
  EMPTY_ARRAY,
  Flex,
  FormMessage,
  HStack,
  Icon,
  IconName,
  isMultileg,
  MarketDataCardDetailsWrapper,
  MarketDataCardExpandDetails,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  UnifiedLiquidityEnum,
  useMixpanel,
  useUnifiedLiquidity,
  type MarketDataCardConfig,
  type Security,
} from '@talos/kyoko';

import { PriceLadder, VolumeLadder } from '@src/components/Ladder';
import { MarketAccountSelectionList } from '@src/components/MarketAccountSelectionList';

import { Page, Pagination, Slide, Slider, Slides } from '@src/components/Slider';

import { ContractDetails } from '@src/components/ContractDetails';

import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { canViewContractDetails } from '../ContractDetails/utils';
import { DeepDiveButton } from '../DeepDive/DeepDiveButton';
import { MultiLegSummary } from '../MultiLegSummary';
import { CandleStickSlide } from './CandleStickSlide';
import { getMultilegUnifiedLiquidityMarketAccounts } from './multilegLegMarketAccounts';
import { MarketsWrapper } from './styles';
import type { OnClickPriceValues } from './types';
import { AVA_MARKET_DATA_CARD_MDS_THROTTLE, type ClickToTradePreviewOrder } from './types';
import { useMarketDataCard } from './useMarketDataCard';

const EXCHANGE_SLIDES = ['Volume Bucketing', 'Price Bucketing', 'Mini Chart', 'Markets'];

export interface MarketDataCardSlidesProps {
  config: MarketDataCardConfig;
  security?: Security;
  activeOrder: ClickToTradePreviewOrder | undefined;
  onClickPrice: (args: { values: OnClickPriceValues; source: string; overrideSide: boolean }) => void;
  onCancelActive: (args?: any) => void;
  onSaveCard: (newConfig: any) => void;
}

export const MarketDataCardSlides = memo(
  ({ config, security, activeOrder, onClickPrice, onCancelActive, onSaveCard }: MarketDataCardSlidesProps) => {
    const {
      id,
      symbol,
      availableMarkets,
      availableMarketAccounts,
      selectedMarkets,
      selectedMarketAccounts,
      selectedAggregation,
      activeSlideIndex,
      detailsExpanded,
      view,
      resolution,
    } = config;

    const sortedAvailableMarketAccounts = useMemo(() => availableMarketAccounts.toSorted(), [availableMarketAccounts]);
    const sortedAvailableMarkets = useMemo(() => availableMarkets.toSorted(), [availableMarkets]);

    const { useNewMarketSelector } = useTradingSettings();

    const mixpanel = useMixpanel();
    const [error, setError] = useState<string | null>(null);

    const [showContractDetails, setShowContractDetails] = useState(!!detailsExpanded);

    const { canViewOrder, canViewCandleStickChart } = useMarketDataCard(security);

    const showDetails = view !== CardViewEnum.COMPACT && canViewContractDetails(security);

    const handleVolumeLadderClickPrice = useCallback(
      (values: OnClickPriceValues, index: number) => {
        onClickPrice({
          values: { ...values, sourceIndex: index },
          source: MixpanelEventSource.VolumeLadder,
          overrideSide: false,
        });
      },
      [onClickPrice]
    );

    const handlePriceLadderClickPrice = useCallback(
      (values: OnClickPriceValues, index: number) => {
        onClickPrice({
          values: { ...values, sourceIndex: index },
          source: MixpanelEventSource.PriceLadder,
          overrideSide: false,
        });
      },
      [onClickPrice]
    );

    const handleMarketAccountsChange = useCallback(
      (newSelectedMarketAccounts: string[]) => {
        if (newSelectedMarketAccounts.length === 0 && selectedMarkets.length === 0) {
          return setError('At least 1 market / market account is required');
        }
        setError(null);
        const newConfig = { ...config, selectedMarketAccounts: newSelectedMarketAccounts };
        onSaveCard(newConfig);
      },
      [selectedMarkets, config, onSaveCard]
    );

    const handleMarketsChange = useCallback(
      (newSelectedMarkets: string[]) => {
        if (newSelectedMarkets.length === 0 && selectedMarketAccounts.length === 0) {
          return setError('At least 1 market / market account is required');
        }
        setError(null);
        const newConfig = { ...config, selectedMarkets: newSelectedMarkets };
        onSaveCard(newConfig);
      },
      [selectedMarketAccounts, config, onSaveCard]
    );

    const handleResolutionChange = useCallback(
      (resolution: ResolutionEnumNoPrefix) => {
        const newConfig = { ...config, resolution };
        onSaveCard(newConfig);
      },
      [config, onSaveCard]
    );

    // When we release the new market selector, we are intending to remove this "markets" slide probably. At least for now its not made compatible, so we hide it.
    const canViewMarketsSlide = !useNewMarketSelector;

    const enableUnifiedLiquidity = config.unifiedLiquidity === UnifiedLiquidityEnum.Enabled;
    const unifiedLiquidityToken = useUnifiedLiquidity(symbol);
    const unifiedLiquidityMarkets = useMemo(
      () => getMultilegUnifiedLiquidityMarketAccounts(selectedMarketAccounts),
      [selectedMarketAccounts]
    );

    return (
      <>
        <Slider data-testid="market-data-card-slides">
          <Slides>
            <Slide isActive={activeSlideIndex === 1}>
              {activeSlideIndex === 1 && security && (
                <VolumeLadder
                  config={config}
                  security={security}
                  isReadOnly={!canViewOrder}
                  activeOrder={activeOrder}
                  marketDataSnapshotThrottle={AVA_MARKET_DATA_CARD_MDS_THROTTLE}
                  onClickPrice={handleVolumeLadderClickPrice}
                  onMouseLeavePrice={onCancelActive}
                  onSaveCard={onSaveCard}
                />
              )}
            </Slide>
            <Slide isActive={activeSlideIndex === 2}>
              {activeSlideIndex === 2 && security && (
                <PriceLadder
                  config={config}
                  security={security}
                  isReadOnly={!canViewOrder}
                  activeOrder={activeOrder}
                  onClickPrice={handlePriceLadderClickPrice}
                  onMouseLeavePrice={onCancelActive}
                  onSaveCard={onSaveCard}
                />
              )}
            </Slide>
            {canViewCandleStickChart && (
              <Slide isActive={activeSlideIndex === 3}>
                {activeSlideIndex === 3 && security && (
                  <CandleStickSlide
                    resolution={resolution ?? '1M'}
                    onChangeResolution={handleResolutionChange}
                    security={security}
                    markets={selectedMarkets}
                    marketAccounts={selectedMarketAccounts}
                  />
                )}
              </Slide>
            )}
            {canViewMarketsSlide && (
              <Slide isActive={activeSlideIndex === 4} overflow="auto">
                {activeSlideIndex === 4 && (
                  <MarketsWrapper>
                    {isMultileg(security) ? (
                      <Box>
                        <MultiLegSummary
                          security={security}
                          readOnly={true}
                          touched={false}
                          supportsAllMarkets={true}
                          unifiedLiquidityToken={enableUnifiedLiquidity ? unifiedLiquidityToken : undefined}
                          unifiedLiquidityMarkets={unifiedLiquidityMarkets}
                          allocations={EMPTY_ARRAY}
                        />
                      </Box>
                    ) : (
                      <MarketAccountSelectionList
                        availableMarketAccounts={sortedAvailableMarketAccounts}
                        availableMarkets={sortedAvailableMarkets}
                        selectedMarketAccounts={selectedMarketAccounts}
                        selectedMarkets={selectedMarkets}
                        security={security}
                        onChangeMarketAccounts={handleMarketAccountsChange}
                        onChangeMarkets={handleMarketsChange}
                        connectionType={ConnectionType.MarketData}
                        showInactiveMarketAccounts={false}
                      />
                    )}
                    {error && (
                      <FormMessage error mt="spacing4">
                        {error}
                      </FormMessage>
                    )}
                  </MarketsWrapper>
                )}
              </Slide>
            )}
          </Slides>
          <Flex alignItems="center" px="spacing16" h="100%">
            <HStack justifyContent="flex-start" flex="1 0 0">
              {showDetails && (
                <MarketDataCardExpandDetails onClick={() => handleShowContractDetails(!showContractDetails)}>
                  <Icon icon={showContractDetails ? IconName.ChevronUp : IconName.ChevronDown} /> Contract Details
                </MarketDataCardExpandDetails>
              )}
            </HStack>
            <HStack>
              <Pagination data-testid="market-data-cards-pagination">
                <Page
                  onClick={() => handleSlideChange(1)}
                  isActive={activeSlideIndex === 1}
                  data-testid="slide-volume-ladder"
                />
                <Page
                  onClick={() => handleSlideChange(2)}
                  isActive={activeSlideIndex === 2}
                  data-testid="slide-price-ladder"
                />
                {canViewCandleStickChart && (
                  <Page
                    onClick={() => handleSlideChange(3)}
                    isActive={activeSlideIndex === 3}
                    data-testid="slide-candlestick"
                  />
                )}
                {canViewMarketsSlide && (
                  <Page
                    onClick={() => handleSlideChange(4)}
                    isActive={activeSlideIndex === 4}
                    data-testid="slide-markets"
                  />
                )}
              </Pagination>
            </HStack>
            <HStack justifyContent="flex-end" flex="1 0 0" gap="spacing8">
              <DeepDiveButton
                security={security}
                selectedMarketAccounts={selectedMarketAccounts}
                selectedMarkets={selectedMarkets}
                selectedAggregation={selectedAggregation}
              />
            </HStack>
          </Flex>
        </Slider>
        {showContractDetails && showDetails && symbol != null && (
          <MarketDataCardDetailsWrapper>
            <ContractDetails symbol={symbol} selectedMarketAccounts={selectedMarketAccounts} tag={id} />
          </MarketDataCardDetailsWrapper>
        )}
      </>
    );

    function handleSlideChange(newSlide: number) {
      if (newSlide === activeSlideIndex) {
        return;
      }
      const newConfig = { ...config, activeSlideIndex: newSlide };
      mixpanel.track(MixpanelEvent.ChangeCardSlide, {
        [MixpanelEventProperty.TabIndex]: newSlide,
        [MixpanelEventProperty.TabLabel]: EXCHANGE_SLIDES[newSlide - 1],
      });
      onSaveCard(newConfig);
      setError(null);
    }

    function handleShowContractDetails(showContractDetails: boolean) {
      const newConfig = { ...config, detailsExpanded: showContractDetails };
      mixpanel.track(MixpanelEvent.ExpandMarketCardContractDetails, {
        [MixpanelEventProperty.Expanded]: showContractDetails,
      });
      setShowContractDetails(showContractDetails);
      onSaveCard(newConfig);
    }
  }
);
